import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  Save,
  X,
  Plus,
  Calendar,
  Clock,
  User,
  Target,
  AlertTriangle,
  FileText,
  Settings,
  Tag,
  Link,
  Paperclip,
  MessageSquare,
  CheckCircle,
  Building,
  Phone,
  Mail,
  MapPin,
  DollarSign,
  Users,
  Briefcase,
  Shield,
  BarChart3,
  Upload,
  Star,
  Award,
  UserCheck,
  FileCheck,
  CreditCard,
  Globe,
  Lock,
  Eye,
  Edit,
  Trash2,
  TrendingUp,
  TrendingDown,
  PieChart,
  Activity,
  Zap,
  Award as AwardIcon,
  ThumbsUp,
  ThumbsDown,
  AlertCircle,
  CheckCircle2,
  XCircle,
  FileSearch,
  Clipboard,
  BookOpen,
  Scale,
  Gavel,
  Badge,
  Certificate,
  Stamp,
  Seal,
  Receipt,
  Banknote,
  Wallet,
  Coins,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Calculator,
  Percent,
  Hash,
  Calendar as CalendarIcon,
  Clock as ClockIcon
} from 'lucide-react';
import { useToast } from '../common/Toast';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const CreateBillingRecord = () => {
  const navigate = useNavigate();
  const { success, error: showError } = useToast();
  
  // Tab management
  const [activeTab, setActiveTab] = useState('basic');
  
  // Form state
  const [formData, setFormData] = useState({
    // Basic Information
    invoiceNumber: '',
    invoiceDate: '',
    dueDate: '',
    status: 'draft',
    priority: 'medium',
    billingType: 'invoice',
    
    // Subcontractor Information
    subcontractor: {
      id: '',
      name: '',
      company: '',
      contactPerson: '',
      email: '',
      phone: '',
      billingAddress: ''
    },
    
    // Project Information
    project: {
      id: '',
      name: '',
      description: '',
      startDate: '',
      endDate: '',
      budget: '',
      status: ''
    },
    
    // Billing Details
    billingDetails: {
      subtotal: 0,
      taxRate: 0,
      taxAmount: 0,
      discountRate: 0,
      discountAmount: 0,
      totalAmount: 0,
      currency: 'USD',
      paymentTerms: '',
      lateFeeRate: 0,
      lateFeeAmount: 0
    },
    
    // Line Items
    lineItems: [],
    
    // Payment Information
    payment: {
      method: '',
      status: 'pending',
      dueDate: '',
      paidDate: '',
      amount: 0,
      reference: '',
      notes: ''
    },
    
    // Financial Details
    financial: {
      accountNumber: '',
      routingNumber: '',
      bankName: '',
      swiftCode: '',
      taxId: '',
      businessLicense: ''
    },
    
    // Terms and Conditions
    terms: {
      paymentTerms: '',
      latePaymentPenalty: '',
      earlyPaymentDiscount: '',
      cancellationPolicy: '',
      refundPolicy: '',
      warrantyTerms: ''
    },
    
    // Attachments and Documents
    documents: [],
    attachments: [],
    receipts: [],
    contracts: [],
    
    // Approval and Review
    approval: {
      approver: '',
      approvalDate: '',
      status: 'pending',
      notes: '',
      nextReviewDate: ''
    },
    
    // Notifications and Reminders
    notifications: {
      emailReminders: true,
      smsReminders: false,
      portalNotifications: true,
      reminderFrequency: 'weekly',
      escalationEnabled: true
    },
    
    // Advanced Settings
    settings: {
      autoCalculate: true,
      requireApproval: true,
      publicVisibility: false,
      allowPartialPayments: false,
      recurringBilling: false
    },
    
    // Recurring Billing (if applicable)
    recurring: {
      enabled: false,
      frequency: 'monthly',
      startDate: '',
      endDate: '',
      nextBillingDate: '',
      cycles: 0
    },
    
    // Tax Information
    tax: {
      taxExempt: false,
      taxId: '',
      taxRate: 0,
      taxAmount: 0,
      taxCategory: '',
      taxJurisdiction: ''
    },
    
    // Discounts and Credits
    discounts: [],
    credits: [],
    adjustments: [],
    
    // Payment History
    paymentHistory: [],
    
    // Compliance and Audit
    compliance: {
      auditTrail: true,
      complianceRequired: false,
      regulatoryFramework: '',
      auditDate: '',
      auditor: ''
    }
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Tab configuration
  const tabs = [
    {
      id: 'basic',
      label: 'Billing Details',
      icon: <Receipt size={18} />
    },
    {
      id: 'items',
      label: 'Line Items',
      icon: <FileText size={18} />
    },
    {
      id: 'payment',
      label: 'Payment & Terms',
      icon: <CreditCard size={18} />
    },
    {
      id: 'settings',
      label: 'Settings & Approval',
      icon: <Settings size={18} />
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedInputChange = (parentField, childField, value) => {
    setFormData(prev => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [childField]: value
      }
    }));
  };

  const handleArrayAdd = (field, value) => {
    if (value && value.trim()) {
      setFormData(prev => ({
        ...prev,
        [field]: [...(prev[field] || []), value.trim()]
      }));
    }
  };

  const handleArrayRemove = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: (prev[field] || []).filter((_, i) => i !== index)
    }));
  };

  const handleLineItemAdd = () => {
    const newItem = {
      id: Date.now(),
      description: '',
      quantity: 1,
      unitPrice: 0,
      total: 0
    };
    
    setFormData(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, newItem]
    }));
  };

  const handleLineItemChange = (index, field, value) => {
    setFormData(prev => {
      const newLineItems = [...prev.lineItems];
      newLineItems[index] = {
        ...newLineItems[index],
        [field]: value
      };
      
      // Calculate total for this line item
      if (field === 'quantity' || field === 'unitPrice') {
        const quantity = field === 'quantity' ? parseFloat(value) || 0 : newLineItems[index].quantity;
        const unitPrice = field === 'unitPrice' ? parseFloat(value) || 0 : newLineItems[index].unitPrice;
        newLineItems[index].total = quantity * unitPrice;
      }
      
      return {
        ...prev,
        lineItems: newLineItems
      };
    });
  };

  const handleLineItemRemove = (index) => {
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, i) => i !== index)
    }));
  };

  const calculateTotals = () => {
    const subtotal = formData.lineItems.reduce((sum, item) => sum + (item.total || 0), 0);
    const taxAmount = (subtotal * formData.billingDetails.taxRate) / 100;
    const discountAmount = (subtotal * formData.billingDetails.discountRate) / 100;
    const totalAmount = subtotal + taxAmount - discountAmount;
    
    setFormData(prev => ({
      ...prev,
      billingDetails: {
        ...prev.billingDetails,
        subtotal,
        taxAmount,
        discountAmount,
        totalAmount
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Calculate totals before submission
      calculateTotals();
      
      // Format data for backend
      const billingData = {
        ...formData,
        // Add any data transformation here
      };

      success('Billing record created successfully!');
      navigate('/subcontracting/billing');
    } catch (err) {
      showError('Failed to create billing record. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return renderBillingDetails();
      case 'items':
        return renderLineItems();
      case 'payment':
        return renderPaymentTerms();
      case 'settings':
        return renderSettingsApproval();
      default:
        return renderBillingDetails();
    }
  };

  const renderBillingDetails = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Billing Information</h3>
        <p className="section-description">Basic billing information and invoice details</p>
      </div>
      
      <div className="form-grid-4">
        <div className="form-group">
          <label className="form-label">Invoice Number *</label>
          <input
            type="text"
            className="form-control"
            value={formData.invoiceNumber}
            onChange={(e) => handleInputChange('invoiceNumber', e.target.value)}
            placeholder="Enter invoice number"
            required
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Invoice Date *</label>
          <input
            type="date"
            className="form-control"
            value={formData.invoiceDate}
            onChange={(e) => handleInputChange('invoiceDate', e.target.value)}
            required
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Due Date *</label>
          <input
            type="date"
            className="form-control"
            value={formData.dueDate}
            onChange={(e) => handleInputChange('dueDate', e.target.value)}
            required
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Status</label>
          <select
            className="form-select"
            value={formData.status}
            onChange={(e) => handleInputChange('status', e.target.value)}
          >
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Billing Type</label>
          <select
            className="form-select"
            value={formData.billingType}
            onChange={(e) => handleInputChange('billingType', e.target.value)}
          >
            <option value="invoice">Invoice</option>
            <option value="credit-note">Credit Note</option>
            <option value="debit-note">Debit Note</option>
            <option value="quote">Quote</option>
            <option value="estimate">Estimate</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Priority</label>
          <select
            className="form-select"
            value={formData.priority}
            onChange={(e) => handleInputChange('priority', e.target.value)}
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Subcontractor Information</h4>
        <div className="form-grid-4">
          <div className="form-group">
            <label className="form-label">Subcontractor *</label>
            <select
              className="form-select"
              value={formData.subcontractor.id}
              onChange={(e) => handleNestedInputChange('subcontractor', 'id', e.target.value)}
              required
            >
              <option value="">Select subcontractor</option>
              <option value="sub-001">ABC Construction Ltd.</option>
              <option value="sub-002">XYZ Engineering Co.</option>
              <option value="sub-003">DEF Services Inc.</option>
              <option value="sub-004">GHI Solutions LLC</option>
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">Contact Person</label>
            <input
              type="text"
              className="form-control"
              value={formData.subcontractor.contactPerson}
              onChange={(e) => handleNestedInputChange('subcontractor', 'contactPerson', e.target.value)}
              placeholder="Enter contact person name"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-control"
              value={formData.subcontractor.email}
              onChange={(e) => handleNestedInputChange('subcontractor', 'email', e.target.value)}
              placeholder="Enter email address"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input
              type="tel"
              className="form-control"
              value={formData.subcontractor.phone}
              onChange={(e) => handleNestedInputChange('subcontractor', 'phone', e.target.value)}
              placeholder="Enter phone number"
            />
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Project Information</h4>
        <div className="form-grid-4">
          <div className="form-group">
            <label className="form-label">Project</label>
            <select
              className="form-select"
              value={formData.project.id}
              onChange={(e) => handleNestedInputChange('project', 'id', e.target.value)}
            >
              <option value="">Select project</option>
              <option value="proj-001">Office Building Construction</option>
              <option value="proj-002">Residential Complex</option>
              <option value="proj-003">Infrastructure Development</option>
              <option value="proj-004">Renovation Project</option>
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">Project Budget</label>
            <input
              type="number"
              className="form-control"
              value={formData.project.budget}
              onChange={(e) => handleNestedInputChange('project', 'budget', e.target.value)}
              placeholder="Enter project budget"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Project Status</label>
            <select
              className="form-select"
              value={formData.project.status}
              onChange={(e) => handleNestedInputChange('project', 'status', e.target.value)}
            >
              <option value="">Select status</option>
              <option value="planning">Planning</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="on-hold">On Hold</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderLineItems = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Line Items</h3>
        <p className="section-description">Invoice line items and billing details</p>
      </div>
      
      <div className="form-group">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="section-subtitle">Invoice Items</h4>
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={handleLineItemAdd}
          >
            <Plus size={16} />
            Add Item
          </button>
        </div>
        
        {formData.lineItems.map((item, index) => (
          <div key={item.id} className="line-item-card mb-3">
            <div className="form-grid-4">
              <div className="form-group">
                <label className="form-label">Description</label>
                <input
                  type="text"
                  className="form-control"
                  value={item.description}
                  onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                  placeholder="Enter item description"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Quantity</label>
                <input
                  type="number"
                  className="form-control"
                  value={item.quantity}
                  onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                  min="0"
                  step="0.01"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Unit Price</label>
                <input
                  type="number"
                  className="form-control"
                  value={item.unitPrice}
                  onChange={(e) => handleLineItemChange(index, 'unitPrice', e.target.value)}
                  min="0"
                  step="0.01"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Total</label>
                <div className="input-group">
                  <input
                    type="number"
                    className="form-control"
                    value={item.total}
                    readOnly
                  />
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() => handleLineItemRemove(index)}
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Billing Summary</h4>
        <div className="form-grid-4">
          <div className="form-group">
            <label className="form-label">Subtotal</label>
            <input
              type="number"
              className="form-control"
              value={formData.billingDetails.subtotal}
              readOnly
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Tax Rate (%)</label>
            <input
              type="number"
              className="form-control"
              value={formData.billingDetails.taxRate}
              onChange={(e) => handleNestedInputChange('billingDetails', 'taxRate', e.target.value)}
              min="0"
              max="100"
              step="0.01"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Tax Amount</label>
            <input
              type="number"
              className="form-control"
              value={formData.billingDetails.taxAmount}
              readOnly
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Discount Rate (%)</label>
            <input
              type="number"
              className="form-control"
              value={formData.billingDetails.discountRate}
              onChange={(e) => handleNestedInputChange('billingDetails', 'discountRate', e.target.value)}
              min="0"
              max="100"
              step="0.01"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Discount Amount</label>
            <input
              type="number"
              className="form-control"
              value={formData.billingDetails.discountAmount}
              readOnly
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Total Amount</label>
            <input
              type="number"
              className="form-control"
              value={formData.billingDetails.totalAmount}
              readOnly
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderPaymentTerms = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Payment & Terms</h3>
        <p className="section-description">Payment information, terms, and conditions</p>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Payment Information</h4>
        <div className="form-grid-4">
          <div className="form-group">
            <label className="form-label">Payment Method</label>
            <select
              className="form-select"
              value={formData.payment.method}
              onChange={(e) => handleNestedInputChange('payment', 'method', e.target.value)}
            >
              <option value="">Select payment method</option>
              <option value="bank-transfer">Bank Transfer</option>
              <option value="check">Check</option>
              <option value="credit-card">Credit Card</option>
              <option value="cash">Cash</option>
              <option value="other">Other</option>
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">Payment Status</label>
            <select
              className="form-select"
              value={formData.payment.status}
              onChange={(e) => handleNestedInputChange('payment', 'status', e.target.value)}
            >
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="partial">Partial</option>
              <option value="overdue">Overdue</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">Payment Amount</label>
            <input
              type="number"
              className="form-control"
              value={formData.payment.amount}
              onChange={(e) => handleNestedInputChange('payment', 'amount', e.target.value)}
              min="0"
              step="0.01"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Payment Reference</label>
            <input
              type="text"
              className="form-control"
              value={formData.payment.reference}
              onChange={(e) => handleNestedInputChange('payment', 'reference', e.target.value)}
              placeholder="Enter payment reference"
            />
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Financial Details</h4>
        <div className="form-grid-4">
          <div className="form-group">
            <label className="form-label">Bank Name</label>
            <input
              type="text"
              className="form-control"
              value={formData.financial.bankName}
              onChange={(e) => handleNestedInputChange('financial', 'bankName', e.target.value)}
              placeholder="Enter bank name"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Account Number</label>
            <input
              type="text"
              className="form-control"
              value={formData.financial.accountNumber}
              onChange={(e) => handleNestedInputChange('financial', 'accountNumber', e.target.value)}
              placeholder="Enter account number"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Routing Number</label>
            <input
              type="text"
              className="form-control"
              value={formData.financial.routingNumber}
              onChange={(e) => handleNestedInputChange('financial', 'routingNumber', e.target.value)}
              placeholder="Enter routing number"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">SWIFT Code</label>
            <input
              type="text"
              className="form-control"
              value={formData.financial.swiftCode}
              onChange={(e) => handleNestedInputChange('financial', 'swiftCode', e.target.value)}
              placeholder="Enter SWIFT code"
            />
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Terms and Conditions</h4>
        <div className="form-grid-2">
          <div className="form-group">
            <label className="form-label">Payment Terms</label>
            <textarea
              className="form-control"
              rows="3"
              value={formData.terms.paymentTerms}
              onChange={(e) => handleNestedInputChange('terms', 'paymentTerms', e.target.value)}
              placeholder="Enter payment terms"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Late Payment Penalty</label>
            <textarea
              className="form-control"
              rows="3"
              value={formData.terms.latePaymentPenalty}
              onChange={(e) => handleNestedInputChange('terms', 'latePaymentPenalty', e.target.value)}
              placeholder="Enter late payment penalty terms"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderSettingsApproval = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Settings & Approval</h3>
        <p className="section-description">Approval settings, notifications, and advanced options</p>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Approval Process</h4>
        <div className="form-grid-4">
          <div className="form-group">
            <label className="form-label">Approver</label>
            <select
              className="form-select"
              value={formData.approval.approver}
              onChange={(e) => handleNestedInputChange('approval', 'approver', e.target.value)}
            >
              <option value="">Select approver</option>
              <option value="john-doe">John Doe</option>
              <option value="jane-smith">Jane Smith</option>
              <option value="mike-johnson">Mike Johnson</option>
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">Approval Date</label>
            <input
              type="date"
              className="form-control"
              value={formData.approval.approvalDate}
              onChange={(e) => handleNestedInputChange('approval', 'approvalDate', e.target.value)}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Approval Status</label>
            <select
              className="form-select"
              value={formData.approval.status}
              onChange={(e) => handleNestedInputChange('approval', 'status', e.target.value)}
            >
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="under-review">Under Review</option>
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">Next Review Date</label>
            <input
              type="date"
              className="form-control"
              value={formData.approval.nextReviewDate}
              onChange={(e) => handleNestedInputChange('approval', 'nextReviewDate', e.target.value)}
            />
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Approval Notes</label>
        <textarea
          className="form-control"
          rows="4"
          value={formData.approval.notes}
          onChange={(e) => handleNestedInputChange('approval', 'notes', e.target.value)}
          placeholder="Enter approval notes and comments"
        />
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Notification Settings</h4>
        <div className="checkbox-group">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={formData.notifications.emailReminders}
              onChange={(e) => handleNestedInputChange('notifications', 'emailReminders', e.target.checked)}
            />
            <label className="form-check-label">Email Reminders</label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={formData.notifications.smsReminders}
              onChange={(e) => handleNestedInputChange('notifications', 'smsReminders', e.target.checked)}
            />
            <label className="form-check-label">SMS Reminders</label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={formData.notifications.portalNotifications}
              onChange={(e) => handleNestedInputChange('notifications', 'portalNotifications', e.target.checked)}
            />
            <label className="form-check-label">Portal Notifications</label>
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Advanced Settings</h4>
        <div className="checkbox-group">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={formData.settings.autoCalculate}
              onChange={(e) => handleNestedInputChange('settings', 'autoCalculate', e.target.checked)}
            />
            <label className="form-check-label">Auto Calculate</label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={formData.settings.requireApproval}
              onChange={(e) => handleNestedInputChange('settings', 'requireApproval', e.target.checked)}
            />
            <label className="form-check-label">Require Approval</label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={formData.settings.allowPartialPayments}
              onChange={(e) => handleNestedInputChange('settings', 'allowPartialPayments', e.target.checked)}
            />
            <label className="form-check-label">Allow Partial Payments</label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={formData.settings.recurringBilling}
              onChange={(e) => handleNestedInputChange('settings', 'recurringBilling', e.target.checked)}
            />
            <label className="form-check-label">Recurring Billing</label>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader 
        title="Billing Record"
        description="Comprehensive billing and payment management system"
      />
      <div className="page-container" style={{ width: '100%', maxWidth: '100%' }}>
        {/* Main Content */}
        <div className="project-create-container" style={{ width: '100%', maxWidth: '100%' }}>
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          {/* Tab Navigation */}
          <div className="project-tabs">
            <div className="project-tabs-nav">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`project-tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <div className="project-tab-icon">
                    {tab.icon}
                  </div>
                  <div className="project-tab-content">
                    <div className="project-tab-label">{tab.label}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="project-form-card">
            {renderTabContent()}
          </div>

          {/* Form Actions */}
          <div className="project-form-actions">
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => navigate('/subcontracting/billing')}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Creating Record...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Create Billing Record
                </>
              )}
            </button>
          </div>
        </form>
        </div>
      </div>
    </>
  );
};

export default CreateBillingRecord;
